import { InputAdornment, TextField } from "@mui/material";
import PropTypes from "prop-types";
import { Controller, useFormContext } from "react-hook-form";
import Iconify from "../Iconify";

RHFTextField.propTypes = {
  name: PropTypes.string,
};

export default function RHFTextField({
  name,
  sx,
  helperText,
  helperRenderer,
  showSuccessAdornment = true,
  successCondition,
  InputProps,
  ...other
}) {
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error, isTouched, isDirty }, formState: { isSubmitted } }) => {
        const value = field.value;
        const hasValue = typeof value === "string" ? value.trim().length > 0 : Boolean(value);
        const showError = Boolean(error) && (isTouched || isSubmitted);
        // For email fields, the green check must only show on a complete valid email,
        // even when the Yup schema lacks .email() (defense in depth across forms).
        const isEmailField = other.type === "email" || /(^|_|-)email(_|-|$|address)/i.test(name || "");
        const emailIsValid = !isEmailField || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
        const passedValidation = !error && (isDirty || isTouched) && hasValue && emailIsValid;
        const customSuccess =
          typeof successCondition === "function" ? successCondition(value) : true;
        const rendered = helperRenderer
          ? helperRenderer({ value, isValid: passedValidation && customSuccess })
          : helperText;
        const successIcon = showSuccessAdornment && passedValidation && customSuccess && !other.disabled && (
          <InputAdornment position="end">
            <Iconify
              icon="eva:checkmark-circle-2-fill"
              sx={{ color: "success.main", width: 20, height: 20 }}
            />
          </InputAdornment>
        );
        const endAdornment = InputProps?.endAdornment || successIcon ? (
          <>
            {InputProps?.endAdornment}
            {successIcon}
          </>
        ) : undefined;
        return (
          <TextField
            {...field}
            fullWidth
            error={showError}
            helperText={showError ? error.message : rendered}
            InputProps={{
              ...InputProps,
              ...(endAdornment ? { endAdornment } : {}),
            }}
            sx={sx}
            {...other}
          />
        );
      }}
    />
  );
}
